export interface FormOwnershipProperty {
    name: string;
    title: string;
    isRepeatable: boolean;
    type: string;
    encrypted?: boolean;
}

export interface FormOwnershipModel {
    modelDefinitionId: string | number;
    modelName: string;
    title: string;
    properties: FormOwnershipProperty[];
}

export interface FormOwnershipRule {
    property: string;
    identity: 'npi' | 'providerId';
    indexes?: 'all' | number[];
    where?: { property: string; equals: string };
}

export interface FormOwnershipModelRules {
    modelName: string;
    rules: FormOwnershipRule[];
}

export interface FormOwnershipSettings {
    version: 1;
    mode: 'defaults' | 'custom' | 'disabled';
    models: FormOwnershipModelRules[];
}

export class FormOwnershipConfigurationError extends Error {}

const identifierPattern = /^[a-zA-Z_][a-zA-Z0-9_]{0,44}$/;
const identityTypes = new Set(['varchar', 'text', 'char', 'integer', 'int', 'bigint', 'numeric']);

export function isOwnershipProperty(property: FormOwnershipProperty): boolean {
    return identifierPattern.test(property.name) && !property.encrypted && identityTypes.has(property.type);
}

function fail(message: string): never {
    throw new FormOwnershipConfigurationError(message);
}

function record(value: unknown, keys: string[], label: string): Record<string, any> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) fail(`${label} must be an object.`);
    if (Object.keys(value).some((key) => !keys.includes(key))) fail(`${label} contains an unsupported setting.`);
    return value as Record<string, any>;
}

function getProperty(model: FormOwnershipModel, name: unknown): FormOwnershipProperty {
    const property = model.properties.find((candidate) => candidate.name === name);
    if (!property || !isOwnershipProperty(property)) {
        fail(`Model ${model.modelName} has no supported ownership property ${String(name)}.`);
    }
    return property;
}

function normalizeRule(input: unknown, model: FormOwnershipModel): FormOwnershipRule {
    const rule = record(input, ['property', 'identity', 'indexes', 'where'], 'Ownership rule');
    const property = getProperty(model, rule.property);
    if (rule.identity !== 'npi' && rule.identity !== 'providerId') fail('Select NPI or provider ID matching.');
    const result: FormOwnershipRule = { property: property.name, identity: rule.identity };

    if (property.isRepeatable) {
        if (rule.indexes === undefined || rule.indexes === 'all') {
            result.indexes = 'all';
        } else {
            if (!Array.isArray(rule.indexes) || !rule.indexes.length || rule.indexes.length > 100) {
                fail('Specific indexes must contain between 1 and 100 sequence numbers.');
            }
            if (
                rule.indexes.some(
                    (index: unknown) => !Number.isInteger(index) || Number(index) < 1 || Number(index) > 2147483647,
                )
            ) {
                fail('Indexes must be positive whole sequence numbers.');
            }
            result.indexes = [...new Set<number>(rule.indexes)].sort((a, b) => a - b);
        }
    } else if (rule.indexes !== undefined) {
        fail(`Scalar property ${property.name} cannot specify indexes.`);
    }

    if (rule.where !== undefined) {
        const condition = record(rule.where, ['property', 'equals'], 'Ownership condition');
        const companion = getProperty(model, condition.property);
        if (companion.isRepeatable !== property.isRepeatable) {
            fail('An ownership property and its condition must have the same repeatability.');
        }
        if (typeof condition.equals !== 'string' || !condition.equals.trim() || condition.equals.length > 100) {
            fail('An ownership condition requires a nonblank value of at most 100 characters.');
        }
        result.where = { property: companion.name, equals: condition.equals.trim() };
    }
    return result;
}

/** Validate persisted settings as well as writes; invalid settings must never broaden a query. */
export function normalizeFormOwnershipSettings(input: unknown, catalog: FormOwnershipModel[]): FormOwnershipSettings {
    const settings = record(input, ['version', 'mode', 'models'], 'Form ownership settings');
    if (settings.version !== 1) fail('Unsupported form ownership settings version.');
    if (!['defaults', 'custom', 'disabled'].includes(settings.mode)) fail('Invalid form ownership mode.');
    if (!Array.isArray(settings.models) || settings.models.length > 20) fail('Select at most 20 models.');
    if (settings.mode !== 'custom') {
        if (settings.models.length) fail('Only custom settings can contain model rules.');
        return { version: 1, mode: settings.mode, models: [] };
    }
    if (!settings.models.length) fail('Custom settings require at least one model.');
    const seen = new Set<string>();
    const models = settings.models.map((inputModel: unknown): FormOwnershipModelRules => {
        const selected = record(inputModel, ['modelName', 'rules'], 'Ownership model');
        if (typeof selected.modelName !== 'string' || !identifierPattern.test(selected.modelName))
            fail('Invalid model name.');
        const model = catalog.find(
            (candidate) => candidate.modelName.toLowerCase() === selected.modelName.toLowerCase(),
        );
        if (!model || !identifierPattern.test(model.modelName)) fail(`Unknown ownership model ${selected.modelName}.`);
        const key = model.modelName.toLowerCase();
        if (seen.has(key)) fail(`Model ${model.modelName} is selected more than once.`);
        seen.add(key);
        if (!Array.isArray(selected.rules) || !selected.rules.length || selected.rules.length > 50) {
            fail('Each model must have between 1 and 50 ownership rules.');
        }
        return { modelName: model.modelName, rules: selected.rules.map((rule: unknown) => normalizeRule(rule, model)) };
    });
    return { version: 1, mode: 'custom', models };
}

export function getDefaultFormOwnershipRules(catalog: FormOwnershipModel[]): FormOwnershipModelRules[] {
    const model = catalog.find((candidate) => candidate.modelName.toLowerCase() === 'surgery');
    // Older deployed models may lack the default property. Custom settings are always validated strictly.
    if (
        !model ||
        !model.properties.some(
            (property) => property.name === 'anes_prvr_id' && property.isRepeatable && isOwnershipProperty(property),
        )
    )
        return [];
    return [
        {
            modelName: model.modelName,
            rules: [{ property: 'anes_prvr_id', identity: 'providerId', indexes: 'all' }],
        },
    ];
}

export function resolveFormOwnershipSettings(input: unknown, catalog: FormOwnershipModel[]) {
    const settings = normalizeFormOwnershipSettings(input, catalog);
    const effectiveModels = settings.mode === 'defaults' ? getDefaultFormOwnershipRules(catalog) : settings.models;
    return { settings, effectiveModels };
}
